import { useId, type ReactNode } from 'react'
import { View } from 'react-native'

import { captureMessage } from '@/src/config/sentry'

import type { TGradientBackgroundProps } from './GradientBackground.types'

export function BgSvg({ svgStyle, fixed = true }: TGradientBackgroundProps) {
  // We have to use a unique id to avoid conflicts with other svg background elements.
  const uid = useId()
  const id = (name: string) => `${uid}-${name}`
  return (
    <svg
      viewBox="0 0 100 100"
      preserveAspectRatio="none"
      style={{
        width: '100%',
        height: '100%',
        display: 'block',
        position: fixed ? 'fixed' : 'absolute',
        background: '#fff',
        ...svgStyle,
      }}
      xmlns="http://www.w3.org/2000/svg"
    >
      <defs>
        <radialGradient id={id('radial0')} gradientUnits="objectBoundingBox" spreadMethod="pad">
          <stop offset="0%" stopColor="#d2eaff" stopOpacity="0.5" />
          <stop offset="100%" stopColor="#d2eaff" stopOpacity="0" />
        </radialGradient>

        <radialGradient id={id('radial1')} gradientUnits="objectBoundingBox" spreadMethod="pad">
          <stop offset="0%" stopColor="#d2eaff" stopOpacity="0.5" />
          <stop offset="100%" stopColor="#d2eaff" stopOpacity="0" />
        </radialGradient>

        <radialGradient
          id={id('radial2')}
          cx="23.6%"
          cy="50%"
          r="45.33%"
          gradientUnits="objectBoundingBox"
          spreadMethod="pad"
        >
          <stop offset="0%" stopColor="#ff665c" stopOpacity="0.1" />
          <stop offset="100%" stopColor="#ff665c" stopOpacity="0" />
        </radialGradient>

        <radialGradient
          id={id('radial3')}
          cx="63.87%"
          cy="45.41%"
          r="27.6%"
          gradientUnits="objectBoundingBox"
          spreadMethod="pad"
        >
          <stop offset="0%" stopColor="#ff98e2" stopOpacity="0.1" />
          <stop offset="100%" stopColor="#ff98e2" stopOpacity="0" />
        </radialGradient>

        <radialGradient
          id={id('radial4')}
          cx="29.6%"
          cy="31.31%"
          r="30.4%"
          gradientUnits="objectBoundingBox"
          spreadMethod="pad"
        >
          <stop offset="0%" stopColor="#fffbd7" stopOpacity="0.6" />
          <stop offset="100%" stopColor="#fff6a6" stopOpacity="0" />
        </radialGradient>
      </defs>

      <rect x="0" y="0" width="100%" height="100%" fill={`url(#${id('radial0')})`} fillOpacity="0" />
      <rect x="0" y="0" width="100%" height="100%" fill={`url(#${id('radial1')})`} />
      <rect x="0" y="0" width="100%" height="100%" fill={`url(#${id('radial2')})`} />
      <rect x="0" y="0" width="100%" height="100%" fill={`url(#${id('radial3')})`} />
      <rect x="0" y="0" width="100%" height="100%" fill={`url(#${id('radial4')})`} />
    </svg>
  )
}

export const GradientBackground = (props: TGradientBackgroundProps): ReactNode => {
  // SamsungBrowser is ignoring dark mode, it renders the light theme and apply some CSS transformations.
  // Those transformations heavily impact the initial SVG appearance.
  // In order to avoid such transformations, we render a basic view.
  // https://www.v01.io/posts/2024-samsung-internet-dark-mode/
  const isSamsungBrowser = navigator.userAgent.includes('Samsung')
  if (isSamsungBrowser) {
    captureMessage.then((f) => f('[Web:GradientBackgroundWeb] Render white screen for Samsung Browser'))
    return <View className="fixed left-0 top-0 h-full w-full bg-white" />
  }
  return <BgSvg {...props} />
}
